import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedFetch } from './useAuthenticatedFetch';
import { API_URL } from '../utils/config';
import { useAuth } from './useAuth';

interface PollingConfig {
  enabled: boolean;
  interval: number; // milliseconds
  onMessagesPage: boolean;
  currentThreadId?: number;
}

interface UnreadCountResponse {
  success: boolean;
  data?: {
    unreadCount: number;
    unreadThreads: number;
  };
}

export const useMessagePolling = (config: PollingConfig) => {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const lastCheckedRef = useRef<Date>(new Date());

  // Poll for unread count (always active when authenticated)
  const { data: unreadData } = useQuery({
    queryKey: ['messages', 'unread-count'],
    queryFn: async () => {
      if (!isAuthenticated) return { unreadCount: 0, unreadThreads: 0 };
      
      try {
        const response = await authenticatedFetch(
          `${API_URL}/api/store/messages/unread-count`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        if (response?.ok) {
          const data: UnreadCountResponse = await response.json();
          if (data.success && data.data) {
            return data.data;
          }
        }
        return { unreadCount: 0, unreadThreads: 0 };
      } catch (error) {
        console.error('Error fetching unread count:', error);
        return { unreadCount: 0, unreadThreads: 0 };
      }
    },
    refetchInterval: config.enabled && isAuthenticated ? config.interval : false,
    refetchIntervalInBackground: false,
    enabled: config.enabled && isAuthenticated,
    staleTime: config.interval / 2, // Consider data stale after half the interval
  });

  // Poll for thread updates when on messages page
  useEffect(() => {
    if (!config.enabled || !config.onMessagesPage || !isAuthenticated) {
      return;
    }

    const pollThreads = window.setInterval(() => {
      // Invalidate threads query to trigger refetch
      queryClient.invalidateQueries({ queryKey: ['messages', 'threads'] });
      lastCheckedRef.current = new Date();
    }, 30000); // 30 seconds for thread list

    return () => window.clearInterval(pollThreads);
  }, [config.enabled, config.onMessagesPage, isAuthenticated, queryClient]);

  // Poll for new messages in current thread
  useEffect(() => {
    if (!config.enabled || !config.currentThreadId || !isAuthenticated) {
      return;
    }

    const pollMessages = window.setInterval(() => {
      // Invalidate specific thread query to trigger refetch
      queryClient.invalidateQueries({ 
        queryKey: ['messages', 'thread', config.currentThreadId] 
      });
    }, 15000); // 15 seconds for active thread

    return () => window.clearInterval(pollMessages);
  }, [config.enabled, config.currentThreadId, isAuthenticated, queryClient]);

  // Handle visibility change - pause/resume polling
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab is hidden, queries will pause automatically with refetchIntervalInBackground: false
      } else {
        // Tab is visible again, invalidate queries to get fresh data
        if (isAuthenticated) {
          queryClient.invalidateQueries({ queryKey: ['messages'] });
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated, queryClient]);

  // Handle online/offline states
  useEffect(() => {
    const handleOnline = () => {
      if (isAuthenticated) {
        queryClient.invalidateQueries({ queryKey: ['messages'] });
      }
    };

    const handleOffline = () => {
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [isAuthenticated, queryClient]);

  return {
    unreadCount: unreadData?.unreadCount || 0,
    unreadThreads: unreadData?.unreadThreads || 0,
    lastChecked: lastCheckedRef.current,
  };
};

// Convenience hook for notification badge
export const useMessageNotifications = () => {
  const pollingData = useMessagePolling({
    enabled: true,
    interval: 60000, // 60 seconds for badge
    onMessagesPage: false,
  });

  return {
    unreadCount: pollingData.unreadCount,
    unreadThreads: pollingData.unreadThreads,
  };
};